import { useCallback, useLayoutEffect, useRef } from 'react'

/**
 * WHERE FOCUS GOES WHEN A RETRY WORKS AND ITS BUTTON STOPS EXISTING.
 *
 * ── THE LOSS IS NOT `busy` ────────────────────────────────────────────────
 *
 * `Button` already restores focus across a `busy` cycle (`Button.tsx:84-97`),
 * and its own docstring names the case it cannot reach: "a button that UNMOUNTS
 * while busy is not covered and cannot be -- there is nothing left to restore
 * to." That is this case, and there are three routes into it, only one of which
 * involves `busy` at all:
 *
 *   - the journal (`retryBusy={saving}`) disables, the browser blurs one frame
 *     later, and then the notice unmounts;
 *   - the three admin lists pass `retryBusy={error.retry === 'page' && isPaging}`,
 *     so on the LIST retry -- the one an administrator hits on landing -- the
 *     button is never disabled, never blurred, and `Button`'s `heldFocus` is
 *     never armed. It simply unmounts while focused. No change to `Button` can
 *     reach this one;
 *   - `UserDetail` early-returns the whole screen while `isLoading`, so the
 *     button unmounts with everything else.
 *
 * All three end at `<body>`: removing the focused element resets
 * `document.activeElement`, which is never null (trap 42) and falls back to the
 * body. That is what makes `body` the signal here.
 *
 * ── ARMING AND FIRING ARE SPLIT, AND THE SPLIT IS LOAD-BEARING ────────────
 *
 * Arming belongs to `ProblemNotice`: it owns the button, and it is its own
 * decision to render one that creates the defect.
 *
 * Firing cannot live there. A layout-effect CLEANUP runs in the mutation phase,
 * before sibling insertions and before refs attach -- so on the admin screens,
 * where `DataTable` swaps out of its loading state in the same commit, the
 * landing element's ref is still null and `focus()` would be a silent no-op. It
 * would work on the journal, where the target is already mounted, and silently
 * not work on the three admin screens: a test that cannot fail, in the shape
 * this sprint keeps finding. So firing runs from a layout effect declared in the
 * SCREEN, which React runs after every descendant ref is attached -- which is
 * why `ApprovalQueue.tsx:121`, `DataRights.tsx:354` and `LoadMore`
 * (`DataTable.tsx:181`) all do it from the surviving side.
 *
 * ── THE EDGE IS "THE NOTICE WENT AWAY", NOT "THE RETRY SUCCEEDED" ─────────
 *
 * Stated this way deliberately, because the two are not the same and claiming
 * the stronger one would be false. `ApprovalQueue.tsx`'s `loadMore()` calls
 * `setError(null)` synchronously before it awaits anything, and React flushes a
 * discrete event synchronously (trap 37), so on that path the notice is gone
 * before the outcome is known. A user who lands on the region that is about to
 * report the outcome has not been stranded, which is the whole obligation here;
 * a user returned to the top of the document has.
 *
 * A FAILED retry does not reach the effect at all, and that is correct rather
 * than overlooked: on every list path the error is still non-null afterwards
 * (`setError({...})` over an error that was already set), so the notice never
 * unmounted and focus never left the button. `armed` therefore stays true until
 * the next time that error clears -- which is still the outcome of the attempt
 * the user made, and the body-check below is what keeps it from reaching
 * somebody who moved on in the meantime.
 */
export function useRetryLanding(failing: boolean) {
  const target = useRef<HTMLElement | null>(null)
  const armed = useRef(false)

  /**
   * A CALLBACK ref, not a ref object, and that is a typing decision rather than
   * a stylistic one. The journal hands this to a `<span>` on one path and to a
   * `<Notice>`'s `<div>` on the other, and a single `RefObject<HTMLElement>`
   * is assignable to neither `Ref<HTMLSpanElement>` nor `Ref<HTMLDivElement>` —
   * refs are invariant in their element. A callback taking `HTMLElement | null`
   * is assignable to both, because parameters go the other way.
   */
  const landOn = useCallback((el: HTMLElement | null) => { target.current = el }, [])

  /**
   * Whether the button ACTUALLY held focus, recorded at the click rather than
   * assumed: on WebKit a click does not focus a button (trap 33), so a keyboard
   * activation and a tap are different states and only one of them is owed a
   * landing. `LoadMore` captures the same fact in its own click handler
   * (`DataTable.tsx:212`) for the same reason.
   */
  const arm = useCallback((held: boolean) => { armed.current = held }, [])

  useLayoutEffect(() => {
    if (failing) return
    const wasArmed = armed.current
    // Cleared UNCONDITIONALLY, which is `LoadMore`'s fixed bug: leaving it set
    // on the path that does not focus makes it true for the life of the
    // component, and every later clearing of an error becomes a focus move.
    armed.current = false
    if (!wasArmed) return
    // Only when focus actually fell. `document.activeElement` is never null, so
    // `body` is the signal -- and somebody who clicked into something else while
    // the request was in flight keeps their place.
    if (document.activeElement !== document.body) return
    target.current?.focus()
  }, [failing])

  /**
   * A TUPLE, not an object, and that is a lint decision rather than taste.
   * `react-hooks/refs` infers the returned object as ref-like because the hook
   * closes over `useRef`, and then reports every `landing.landOn` in JSX as
   * "Cannot access refs during render" -- a false positive, since both members
   * are `useCallback` results. Destructuring a tuple at the call site does not
   * trip it, and `npm run lint` is in the gate.
   */
  return [landOn, arm] as const
}

/**
 * What `ProblemNotice` needs, and deliberately nothing more: it records whether
 * its button held focus and is structurally incapable of moving focus itself.
 */
export type ArmLanding = (held: boolean) => void
